/**
 * SearchDropdown Component
 *
 * Floating dropdown showing autocomplete results grouped by type.
 * Simple design: subsection headers with dividers, just names (no images/prices).
 * Supports products (watches, straps, eyewear) and stores/retailers.
 */

'use client';

import type { ProductHit } from '@/lib/algolia/types';
import type { StoreHit } from '@/lib/algolia/stores';
import { getLocaleCode } from '@/lib/algolia/config';
import { searchPaths } from '@/lib/paths';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { Link } from '@/lib/i18n/navigation';
import { useParams } from 'next/navigation';
import { forwardRef } from 'react';
import styles from './SearchDropdown.module.scss';

export interface GroupedHits {
  type: 'Watch' | 'Strap' | 'Eyewear' | 'Store';
  hits: ProductHit[];
  totalCount: number;
}

export interface GroupedStores {
  type: 'Store';
  hits: StoreHit[];
  totalCount: number;
}

export interface SearchDropdownProps {
  /** Grouped search results */
  groupedHits: GroupedHits[];
  /** Grouped store results */
  groupedStores?: GroupedStores;
  /** Current search query */
  query: string;
  /** Called when a product is clicked */
  onItemClick?: (hit: ProductHit) => void;
  /** Called when a store is clicked */
  onStoreClick?: (hit: StoreHit) => void;
  /** Called when a group "View all" is clicked */
  onGroupClick?: (type: string) => void;
  /** Called when dropdown should close */
  onClose?: () => void;
  /** When true, product clicks are handled by onItemClick instead of Link navigation */
  preventItemNavigation?: boolean;
  /** Whether the dropdown is visible */
  isVisible?: boolean;
  /** Whether results are loading */
  isLoading?: boolean;
  /** Additional class name */
  className?: string;
}

export const SearchDropdown = forwardRef<HTMLDivElement, SearchDropdownProps>(
  function SearchDropdown(
    {
      groupedHits,
      groupedStores,
      query,
      onItemClick,
      onStoreClick,
      onClose,
      preventItemNavigation = false,
      isVisible = true,
      isLoading = false,
      className,
    },
    ref
  ) {
    const t = useTranslations('search');
    const params = useParams();
    const lang = params.lang as string;
    const localeCode = getLocaleCode(lang);

    if (!isVisible) return null;

    const hasProductResults = groupedHits.some((group) => group.hits.length > 0);
    const hasStoreResults = groupedStores && groupedStores.hits.length > 0;
    const hasResults = hasProductResults || hasStoreResults;

    const handleItemClick = (event: React.MouseEvent<HTMLAnchorElement>, hit: ProductHit) => {
      if (preventItemNavigation) {
        event.preventDefault();
      }
      onItemClick?.(hit);
      onClose?.();
    };

    const handleStoreClick = (hit: StoreHit) => {
      onStoreClick?.(hit);
      onClose?.();
    };

    const buildProductUrl = (hit: ProductHit, productType: string): string => {
      // Navigate to search page with product name as query (not directly to PDP)
      const productName = getProductName(hit);

      if (productType === 'Watch') {
        return searchPaths.watches(productName);
      }
      if (productType === 'Strap') {
        return searchPaths.straps(productName);
      }
      if (productType === 'Eyewear') {
        return searchPaths.eyewear(productName);
      }
      // Default to all search
      return searchPaths.all(productName);
    };

    const buildStoreUrl = (hit: StoreHit): string => {
      return `/boutiques/${hit.slug || hit.objectID}`;
    };

    const getProductName = (hit: ProductHit): string => {
      return hit.name?.[localeCode] || hit.name?.['EN_US'] || hit.objectID;
    };

    const getGroupLabel = (type: string): string => {
      const labels: Record<string, string> = {
        Watch: t('tabs.watches'),
        Strap: t('tabs.straps'),
        Eyewear: t('tabs.eyewear'),
        Store: t('retailers'),
      };
      return labels[type] || type;
    };

    return (
      <div
        ref={ref}
        className={clsx(styles.dropdown, className)}
        role="listbox"
        aria-label={t('aria.searchInput')}
      >
        {isLoading && (
          <div className={styles.loading}>
            <span className={styles.loadingText}>Loading...</span>
          </div>
        )}

        {!isLoading && !hasResults && query && (
          <div className={styles.noResults}>
            <span className={styles.noResultsText}>{t('dropdown.noMatches')}</span>
          </div>
        )}

        {!isLoading && hasResults && (
          <div className={styles.groups}>
            {/* Product groups */}
            {groupedHits.map((group) => {
              if (group.hits.length === 0) return null;

              return (
                <div key={group.type} className={styles.group}>
                  {/* Subsection header */}
                  <div className={styles.groupHeader}>
                    <span className={styles.groupTitle}>{getGroupLabel(group.type)}</span>
                  </div>

                  {/* Product names - simple list */}
                  <ul className={styles.items} role="group">
                    {group.hits.map((hit) => (
                      <li key={hit.objectID} role="option" aria-selected={false}>
                        <Link
                          href={buildProductUrl(hit, group.type)}
                          className={styles.item}
                          onClick={(event) => handleItemClick(event, hit)}
                        >
                          <span className={styles.itemName}>{getProductName(hit)}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}

            {/* Stores/Retailers group */}
            {hasStoreResults && groupedStores && (
              <div className={styles.group}>
                <div className={styles.groupHeader}>
                  <span className={styles.groupTitle}>{getGroupLabel('Store')}</span>
                </div>

                <ul className={styles.items} role="group">
                  {groupedStores.hits.map((hit) => (
                    <li key={hit.objectID} role="option" aria-selected={false}>
                      <Link
                        href={buildStoreUrl(hit)}
                        className={styles.item}
                        onClick={() => handleStoreClick(hit)}
                      >
                        <span className={styles.itemName}>{hit.name || hit.name_en}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

SearchDropdown.displayName = 'SearchDropdown';
